// TelcoTrack — weld-layout v2 occupancy, free fibers, counts, offers, capacity.

import {
  cabinetCassetteRackForType,
  getRozvadecCabinetType,
  resolveRozvadecCabinetTypeByAlias,
  resolveRozvadecConnectionCapabilities,
  splitterRatioOutputCount,
} from './rozvadec-cabinet.constants';
import { resolveRozvadecLiveWeldCapacity } from './rozvadec-weld-capacity';
import {
  ROZVADEC_CASSETTE_TYPE_IDS,
  canonicalRozvadecCassetteTypeId,
  getRozvadecCassetteType,
  isRozvadecWeldTrayCassette,
  rozvadecCassetteOccupiedPositions,
} from './rozvadec-cassette.constants';
import { fiberCountFromTypOk } from './typ-ok.constants';
import {
  hasWeldCassetteSlot,
  isCompleteWeldIncomingRef,
  isCompleteWeldTargetRef,
  rozvadecWeldCassetteSlotKey,
  rozvadecWeldIncomingKey,
  rozvadecWeldIncomingRefKey,
  rozvadecWeldRackPositionKey,
  rozvadecWeldSplitterOutputKey,
  rozvadecWeldTargetKey,
} from './rozvadec-weld-layout.ids';
import {
  isRozvadecBuildingTargetedLink,
  isRozvadecCassetteBypassDirect,
  isRozvadecOsWeldTrayId,
  rozvadecCassettePairCount,
  weldLinkCassetteId,
  weldLinkIncomingRefs,
} from './rozvadec-weld-layout.pairs';
import { calculateCabinetWeldCount } from './rozvadec-weld-billing';
import {
  isOccupancyFiberFiberPair,
  layoutWeldConnections,
  occupancyFiberIsOutgoing,
  parseOccupancyEndpointId,
} from './rozvadec-occupancy-connection';
import type { RsuOccupancy } from './rozvadec-rsu-occupancy.types';
import type {
  RozvadecWeldCassette,
  RozvadecWeldCassetteCountsByType,
  RozvadecWeldFreeCassetteSlot,
  RozvadecWeldFreeSplitterOutput,
  RozvadecWeldIncomingRef,
  RozvadecWeldLayout,
  RozvadecWeldLayoutBuilding,
  RozvadecWeldLayoutCable,
  RozvadecWeldLayoutCapacity,
  RozvadecWeldLayoutContext,
  RozvadecWeldLayoutCounts,
  RozvadecWeldLayoutOccupancy,
  RozvadecWeldLayoutOffers,
} from './rozvadec-weld-layout.types';

/**
 * Cassette-bypass přímé sváry / napřímo (incoming → building, no KM tray)
 * only on OS (optická spojka). Address cabinets weld only inside KM4/KM6.
 * Untyped / unknown keeps the historic direct-only fallback (cannot prove it is not OS).
 */
export function defaultRozvadecWeldLayoutOffers(
  typeId?: string | null,
): RozvadecWeldLayoutOffers {
  const capabilities = resolveRozvadecConnectionCapabilities({ persistedTypeId: typeId });
  const connectorsEnabled = capabilities.allowedKinds.some(
    (kind) => kind === 'connector' || kind === 'splitter',
  );
  if (isOsDirectOnlyWeldType(typeId)) {
    return { directEnabled: true, connectorsEnabled: false };
  }
  if (connectorsEnabled) {
    return { directEnabled: false, connectorsEnabled: true };
  }
  return { directEnabled: true, connectorsEnabled: false };
}

/** True when the cabinet type is OS / weld-tray (offers.directEnabled). Seat-less KM pairing still uses this gate; new cassette-bypass incoming→building is rejected separately. */
export function rozvadecAllowsNewDirectSplices(typeId?: string | null): boolean {
  return defaultRozvadecWeldLayoutOffers(typeId).directEnabled;
}

/**
 * Drop stored cassette-bypass directs on non-OS cabinets.
 * KM4/KM6 tray welds (pending or paired) are kept. OS / untyped is unchanged.
 */
export function disconnectNonOsCassetteBypassDirects(
  layout: RozvadecWeldLayout,
  typeId?: string | null,
): { layout: RozvadecWeldLayout; removedLinkIds: string[] } {
  if (rozvadecAllowsNewDirectSplices(typeId)) {
    return { layout, removedLinkIds: [] };
  }
  const removedLinkIds: string[] = [];
  const links = layout.links.filter((link) => {
    if (!isRozvadecCassetteBypassDirect(link)) return true;
    const id = (link.id ?? '').trim();
    if (id) removedLinkIds.push(id);
    return false;
  });
  if (removedLinkIds.length === 0) {
    return { layout, removedLinkIds };
  }
  return { layout: { ...layout, links }, removedLinkIds };
}

/**
 * Android visual editor used to persist OS seats as `links[].cassetteId = "os-tray"`
 * (UI-only) and KM4/KM6 pairs as both cassette links *and* occupancy fiber↔fiber.
 * Persist contract: OS tray → `weldConnections`; KM tray → cassette-bound `links`.
 */
export function sanitizeClientWeldLayoutShape(
  layout: RozvadecWeldLayout,
  typeId?: string | null,
): RozvadecWeldLayout {
  const links = layout.links.filter((link) => !isRozvadecOsWeldTrayId(weldLinkCassetteId(link)));
  const welds = layout.weldConnections ?? [];
  const weldConnections = isOsDirectOnlyWeldType(typeId)
    ? welds
    : welds.filter((weld) => {
        const from = parseOccupancyEndpointId(weld.fromEndpointId);
        const to = parseOccupancyEndpointId(weld.toEndpointId);
        return !(from && to && isOccupancyFiberFiberPair(from, to));
      });
  if (links.length === layout.links.length && weldConnections.length === welds.length) {
    return layout;
  }
  return { ...layout, links, weldConnections };
}

export function emptyRozvadecWeldLayout(typeId?: string | null): RozvadecWeldLayout {
  return {
    version: 2,
    offers: defaultRozvadecWeldLayoutOffers(typeId),
    cassettes: [],
    terminations: [],
    links: [],
    weldConnections: [],
    patchConnections: [],
    splitterPaths: [],
  };
}

/**
 * Offers are type-derived, not user-toggled. OS / untyped → direct-only.
 * Address cabinets (PON/MUK/…) keep connectors; cassette-bypass přímé sváry are OS-only.
 * Persisted `offers` stay in the document; load/save rewrite them to these defaults
 * so older layouts with both flags (or a stale “Na přímo” only) behave correctly.
 */
export function enforceRozvadecWeldLayoutOffers(
  _offers: RozvadecWeldLayoutOffers,
  typeId?: string | null,
): RozvadecWeldLayoutOffers {
  return defaultRozvadecWeldLayoutOffers(typeId);
}

/**
 * True only for a real optical splice (OS) / catalog type that allows direct welds only.
 * Unknown / missing typeId is not OS — do not use this for the “Optická spojka…” callout.
 * Untyped cabinets still get direct-only *offers* via `defaultRozvadecWeldLayoutOffers`.
 */
export function isOsDirectOnlyWeldType(typeId?: string | null): boolean {
  const type = getRozvadecCabinetType(typeId) ?? resolveRozvadecCabinetTypeByAlias(typeId);
  if (!type) return false;
  if (type.id === 'OS') return true;
  return type.allowedConnectionKinds.length === 1 && type.allowedConnectionKinds[0] === 'direct';
}

export function resolveWeldFiberCapacity(input: {
  typOk?: string | null;
  fiberCount?: number | null;
}): number | null {
  if (typeof input.fiberCount === 'number' && Number.isInteger(input.fiberCount) && input.fiberCount > 0) {
    return input.fiberCount;
  }
  return fiberCountFromTypOk(input.typOk);
}

export function expandWeldCableFibers(cable: RozvadecWeldLayoutCable): RozvadecWeldIncomingRef[] {
  const cableId = cable.cableId.trim();
  const capacity = resolveWeldFiberCapacity(cable);
  if (!cableId || capacity == null) return [];
  const fibers: RozvadecWeldIncomingRef[] = [];
  for (let physicalFiber = 1; physicalFiber <= capacity; physicalFiber += 1) {
    fibers.push({ cableId, physicalFiber });
  }
  return fibers;
}

export function findWeldLayoutCable(
  cables: readonly RozvadecWeldLayoutCable[] | null | undefined,
  cableId: string,
): RozvadecWeldLayoutCable | null {
  const id = cableId.trim();
  if (!id) return null;
  return (cables ?? []).find((cable) => cable.cableId.trim() === id) ?? null;
}

export function findWeldLayoutBuilding(
  buildings: readonly RozvadecWeldLayoutBuilding[] | null | undefined,
  buildingId: string,
): RozvadecWeldLayoutBuilding | null {
  const id = buildingId.trim();
  if (!id) return null;
  return (buildings ?? []).find((building) => building.buildingId.trim() === id) ?? null;
}

export function emptyRozvadecWeldCassetteCounts(): RozvadecWeldCassetteCountsByType {
  const counts = {} as RozvadecWeldCassetteCountsByType;
  for (const typeId of ROZVADEC_CASSETTE_TYPE_IDS) {
    counts[typeId] = 0;
  }
  return counts;
}

/** Aggregate weld tray capacity (KM4/KM6) of the installed cassettes. */
export function rozvadecWeldTrayCapacity(
  cassettes: readonly Pick<RozvadecWeldCassette, 'cassetteType'>[],
): number {
  let capacity = 0;
  for (const cassette of cassettes) {
    if (!isRozvadecWeldTrayCassette(cassette.cassetteType)) continue;
    capacity += getRozvadecCassetteType(cassette.cassetteType)?.weldCapacity ?? 0;
  }
  return capacity;
}

export function deriveRozvadecWeldLayoutCounts(
  layout: Pick<RozvadecWeldLayout, 'cassettes' | 'terminations' | 'links' | 'weldConnections' | 'patchConnections'>,
  options?: { rsuOccupancy?: Pick<RsuOccupancy, 'weldConnections' | 'patchConnections'> | null },
): RozvadecWeldLayoutCounts {
  const cassetteCountsByType = emptyRozvadecWeldCassetteCounts();
  for (const cassette of layout.cassettes) {
    const typeId = canonicalRozvadecCassetteTypeId(cassette.cassetteType);
    if (typeId && cassetteCountsByType[typeId] != null) {
      cassetteCountsByType[typeId] += 1;
    }
  }
  let connectorTerminationCount = 0;
  let splitterTerminationCount = 0;
  for (const termination of layout.terminations) {
    if (termination.kind === 'connector') connectorTerminationCount += 1;
    else if (termination.kind === 'splitter') splitterTerminationCount += 1;
  }
  let directLinkCount = 0;
  let connectorLinkCount = 0;
  let splitterOutputLinkCount = 0;
  for (const link of layout.links) {
    if (link.kind === 'direct') directLinkCount += 1;
    else if (link.kind === 'connector') connectorLinkCount += 1;
    else if (link.kind === 'splitter') splitterOutputLinkCount += 1;
  }
  const terminationCount = layout.terminations.length;
  const cassettePairCount = rozvadecCassettePairCount(layout);
  const buildingLinkCount = layout.links.filter(isRozvadecBuildingTargetedLink).length;
  const linkCount = layout.links.length;
  return {
    cassetteCount: layout.cassettes.length,
    cassetteCountsByType,
    cassetteWeldCapacity: rozvadecWeldTrayCapacity(layout.cassettes),
    cassettePairCount,
    terminationCount,
    connectorTerminationCount,
    splitterTerminationCount,
    linkCount,
    directLinkCount,
    connectorLinkCount,
    splitterOutputLinkCount,
    weldCountPrimary: terminationCount + cassettePairCount,
    weldCountSecondary: buildingLinkCount,
    weldCount: terminationCount + cassettePairCount + buildingLinkCount,
    weldCountTotal: calculateCabinetWeldCount({
      layout,
      rsuOccupancy: options?.rsuOccupancy,
    }),
  };
}

/** All termination slots one cassette offers (adapters or preinstalled splitters). */
export function rozvadecCassetteTerminationSlotCount(
  cassetteType: string | null | undefined,
): number {
  const def = getRozvadecCassetteType(cassetteType);
  if (!def) return 0;
  if (def.connectorPortCount != null) return def.connectorPortCount;
  if (def.adapterCount != null) return def.adapterCount;
  if (def.preinstalledSplitters != null) return def.preinstalledSplitters.count;
  return 0;
}

function occupancyKeysFromWelds(
  layout: RozvadecWeldLayout,
  buildings: readonly RozvadecWeldLayoutBuilding[] | null | undefined,
): { incomingKeys: string[]; targetKeys: string[]; slotKeys: string[] } {
  const incomingKeys: string[] = [];
  const targetKeys: string[] = [];
  const slotKeys: string[] = [];
  for (const weld of layoutWeldConnections(layout)) {
    for (const raw of [weld.fromEndpointId, weld.toEndpointId]) {
      const parsed = parseOccupancyEndpointId(raw);
      if (!parsed) continue;
      if (parsed.kind === 'cassette-connector') {
        slotKeys.push(rozvadecWeldCassetteSlotKey(parsed.cassetteId, parsed.slotInCassette));
      }
      if (parsed.kind !== 'cable-fiber') continue;
      if (occupancyFiberIsOutgoing(parsed)) {
        const target = targetKeyForWeldFiber(parsed, buildings);
        if (target) targetKeys.push(target);
      } else {
        incomingKeys.push(rozvadecWeldIncomingRefKey({
          cableId: parsed.cableId,
          physicalFiber: parsed.physicalFiber,
        }));
      }
    }
  }
  return { incomingKeys, targetKeys, slotKeys };
}

function targetKeyForWeldFiber(
  fiber: { cableId: string; physicalFiber: number },
  buildings: readonly RozvadecWeldLayoutBuilding[] | null | undefined,
): string | null {
  const cableId = fiber.cableId.trim();
  if (!cableId || !Number.isInteger(fiber.physicalFiber) || fiber.physicalFiber < 1) return null;
  const matched = (buildings ?? []).find((item) => (item.cableId ?? '').trim() === cableId);
  if (matched?.buildingId.trim()) {
    return rozvadecWeldTargetKey({
      buildingId: matched.buildingId.trim(),
      cableId,
      physicalFiber: fiber.physicalFiber,
    });
  }
  if (cableId.startsWith('budova:')) {
    const buildingId = cableId.slice('budova:'.length).trim();
    if (!buildingId) return null;
    return rozvadecWeldTargetKey({ buildingId, cableId, physicalFiber: fiber.physicalFiber });
  }
  return null;
}

export function deriveRozvadecWeldLayoutOccupancy(
  layout: RozvadecWeldLayout,
  context: RozvadecWeldLayoutContext = {},
): RozvadecWeldLayoutOccupancy {
  const weldOccupancy = occupancyKeysFromWelds(layout, context.buildings);
  const occupiedRackPositionKeys = uniqueSortedStrings(
    layout.cassettes.flatMap((cassette) =>
      rozvadecCassetteOccupiedPositions(cassette.cassetteType, cassette.position).map((position) =>
        rozvadecWeldRackPositionKey(cassette.sectionId, position),
      ),
    ),
  );
  const occupiedCassetteSlotKeys = uniqueSortedStrings([
    ...layout.terminations
      .filter((termination) => hasWeldCassetteSlot(termination))
      .map((termination) =>
        rozvadecWeldCassetteSlotKey(
          termination.cassetteId as string,
          termination.slotInCassette as number,
        ),
      ),
    ...weldOccupancy.slotKeys,
  ]);
  const terminatedIncomingKeys = uniqueSortedStrings(
    layout.terminations.flatMap((termination) =>
      isCompleteWeldIncomingRef(termination.incoming)
        ? [rozvadecWeldIncomingRefKey(termination.incoming)]
        : [],
    ),
  );
  const directIncomingKeys = uniqueSortedStrings(
    layout.links.flatMap((link) =>
      weldLinkIncomingRefs(link).map((ref) => rozvadecWeldIncomingRefKey(ref)),
    ),
  );
  const occupiedIncomingKeys = uniqueSortedStrings([
    ...terminatedIncomingKeys,
    ...directIncomingKeys,
    ...weldOccupancy.incomingKeys,
  ]);
  const occupiedTargetKeys = uniqueSortedStrings([
    ...layout.links.flatMap((link) =>
      isCompleteWeldTargetRef(link.target) ? [rozvadecWeldTargetKey(link.target)] : [],
    ),
    ...weldOccupancy.targetKeys,
  ]);
  const occupiedSplitterOutputKeys = uniqueSortedStrings(
    layout.links
      .filter(
        (link) =>
          link.kind === 'splitter' &&
          (link.terminationId ?? '').trim() &&
          link.splitterOutput != null &&
          Number.isInteger(link.splitterOutput),
      )
      .map((link) =>
        rozvadecWeldSplitterOutputKey(link.terminationId as string, link.splitterOutput as number),
      ),
  );

  const linkedConnectorIds = new Set(
    layout.links
      .filter((link) => link.kind === 'connector')
      .map((link) => (link.terminationId ?? '').trim())
      .filter(Boolean),
  );
  const freeConnectorTerminationIds = layout.terminations
    .filter((termination) => termination.kind === 'connector' && !linkedConnectorIds.has(termination.id))
    .map((termination) => termination.id)
    .sort((a, b) => a.localeCompare(b));

  const freeSplitterOutputs: RozvadecWeldFreeSplitterOutput[] = [];
  for (const termination of layout.terminations) {
    if (termination.kind !== 'splitter') continue;
    const outputs = splitterRatioOutputCount(termination.splitterRatio) ?? 0;
    for (let splitterOutput = 1; splitterOutput <= outputs; splitterOutput += 1) {
      const key = rozvadecWeldSplitterOutputKey(termination.id, splitterOutput);
      if (occupiedSplitterOutputKeys.includes(key)) continue;
      freeSplitterOutputs.push({ terminationId: termination.id, splitterOutput });
    }
  }

  const occupiedSlots = new Set(occupiedCassetteSlotKeys);
  const freeCassetteSlots: RozvadecWeldFreeCassetteSlot[] = [];
  for (const cassette of layout.cassettes) {
    const slotCount = rozvadecCassetteTerminationSlotCount(cassette.cassetteType);
    for (let slotInCassette = 1; slotInCassette <= slotCount; slotInCassette += 1) {
      if (occupiedSlots.has(rozvadecWeldCassetteSlotKey(cassette.id, slotInCassette))) continue;
      freeCassetteSlots.push({ cassetteId: cassette.id, slotInCassette });
    }
  }

  const terminated = new Set(terminatedIncomingKeys);
  const usedDirect = new Set(directIncomingKeys);
  const freeIncoming: RozvadecWeldIncomingRef[] = [];
  for (const cable of context.incomingCables ?? []) {
    for (const fiber of expandWeldCableFibers(cable)) {
      if (terminated.has(rozvadecWeldIncomingRefKey(fiber))) continue;
      freeIncoming.push(fiber);
    }
  }
  const availableDirectSources = freeIncoming.filter(
    (fiber) => !usedDirect.has(rozvadecWeldIncomingRefKey(fiber)),
  );

  return {
    occupiedRackPositionKeys,
    occupiedCassetteSlotKeys,
    occupiedIncomingKeys,
    terminatedIncomingKeys,
    directIncomingKeys,
    occupiedTargetKeys,
    occupiedSplitterOutputKeys,
    freeIncoming,
    availableDirectSources,
    freeConnectorTerminationIds,
    freeSplitterOutputs,
    freeCassetteSlots,
  };
}

/**
 * Type-level + live osazení capacity. SDF/RSU type `kapacita` is not a weld count.
 * MUK/PON32 weld limits come from installed cassettes when present.
 */
export function resolveRozvadecWeldLayoutCapacity(
  context: RozvadecWeldLayoutContext,
): RozvadecWeldLayoutCapacity {
  const capabilities = resolveRozvadecConnectionCapabilities({
    persistedTypeId: context.typeId,
    derivedFromSap: context.derivedFromSap,
    materials: context.materials,
  });
  const live = resolveRozvadecLiveWeldCapacity({
    typeId: context.typeId ?? capabilities.typeId,
    derivedFromSap: context.derivedFromSap,
    materials: context.materials,
    cassettes: context.cassettes,
  });
  const weldLimit = capabilities.family === 'os' ? capabilities.weldLimit : live.weldLimit;
  const connectorLimit = live.connectorLimit ?? capabilities.connectorLimit;
  return {
    primaryLimit: weldLimit,
    secondaryLimit: capabilities.family === 'os' ? weldLimit : weldLimit,
    unknownOsLimit: capabilities.unknownOsLimit,
    catalogWeldCapacity: null,
    maxWeldCount: capabilities.maxWeldCount,
    connectorLimit,
    weldLimit,
  };
}

export function incomingKeysForCable(cable: RozvadecWeldLayoutCable): string[] {
  return expandWeldCableFibers(cable).map((fiber) =>
    rozvadecWeldIncomingKey(fiber.cableId, fiber.physicalFiber),
  );
}

/** True when the cabinet type has a cassette rack (MUK / PON32 weld-layout v2 editor). */
export function hasRozvadecCassetteRack(typeId?: string | null): boolean {
  return cabinetCassetteRackForType(typeId) != null;
}

function uniqueSortedStrings(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}
